import React, { useEffect, useRef, useCallback } from 'react';
import UITenantInfo from '@/components/shared/UITenantInfo';
import UIIconButton from '@/components/shared/UIIconButton';
import UIChatInput from '@/components/shared/UIChatInput';
import UIChatMessage from '@/components/shared/UIChatMessage';
import useChats from '@/hooks/useChats';
import useReadChat from '@/hooks/useReadChat';
import { useSelector } from 'react-redux';
import { profileSelector } from '@/store/profile/profileSlices';
import { Message as APIMessage } from '@/types';
import convertToLocaleString from '@/utils/convertToLocaleString';
import type { ConversationData } from '@/components/shared/Chat/ActiveConversationsList';
import './ConversationView.scss';

export interface Message {
  id: number;
  text: string;
  timestamp: string;
  isOwn: boolean;
  sender: {
    name: string;
    avatar?: string | null;
  };
}

export interface ConversationViewProps {
  chatId?: number | null;
  activeConversation?: ConversationData;
  onAttachFile?: () => void;
  onInfoClick?: (tenantId: number) => void;
  className?: string;
}

const baseClass = 'ConversationView';

const ConversationView: React.FC<ConversationViewProps> = ({
                                                             chatId,
                                                             activeConversation,
                                                             onAttachFile = () => console.log('Attach file'),
                                                             onInfoClick = (id) => console.log('Info clicked:', id),
                                                             className = '',
                                                           }) => {
  const profile = useSelector(profileSelector);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const { chat, chatIsLoading, chatIsFetching, sendMessage, isPending } =
    useChats(chatId);

  const { mutate: markAsRead } = useReadChat();

  const containerClasses = [baseClass, className].filter(Boolean).join(' ');

  const handleSendMessage = useCallback(
    (messageText: string) => {
      if (messageText.trim() && chatId) {
        sendMessage({ recipientId: chatId, message: messageText });
      }
    },
    [chatId, sendMessage],
  );

  useEffect(() => {
    if (
      messagesEndRef.current &&
      !chatIsLoading &&
      !chatIsFetching &&
      chat?.messages?.length
    ) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

        if (chat && chat.id) {
            const hasUnread = chat.messages.some((msg: APIMessage) => {
                return !msg.isRead && msg.sender.id !== profile?.id;
            });
            if (hasUnread) {
                markAsRead({ chatId: chat.id });
            }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [chat?.id, chat?.messages, chatIsLoading, chatIsFetching, profile, markAsRead]);

  const convertApiMessages = (apiMessages: APIMessage[]): Message[] => {
    return apiMessages.map((msg) => {
      return {
        id: msg.id,
        text: msg.text,
        timestamp: convertToLocaleString(msg.createdAt),
        isOwn: msg.sender.id === profile?.id,
        sender: {
          name: `${msg.sender.firstname} ${msg.sender.lastname}`,
          avatar: msg.sender.avatar,
        },
      };
    });
  };

  const uniqueMessages = chat?.messages
    ? chat.messages.reduce((acc: APIMessage[], message) => {
      if (!acc.find((m) => m.id === message.id)) {
        acc.push(message);
      }
      return acc;
    }, [])
    : [];

  const messages = convertApiMessages(uniqueMessages);

  if (!activeConversation) {
    return (
        <div className={containerClasses}>
            <div className={`${baseClass}__empty-state`}>
                Conversation not found
            </div>
        </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className={`${baseClass}__header`}>
        <div className={`${baseClass}__header-content`}>
          <UITenantInfo
            name={activeConversation.name}
            email={activeConversation.email}
            tenantId={activeConversation.id}
            avatar={activeConversation.avatar}
            size="medium"
          />

          <div className={`${baseClass}__header-actions`}>
            <UIIconButton
              iconType="userinfo"
              variant="info"
              size="medium"
              onClick={() => onInfoClick(activeConversation.id)}
            />
          </div>
        </div>

        <hr className={`${baseClass}__header-divider`} />
      </div>

      <div className={`${baseClass}__chat`}>
        {chatIsLoading || chatIsFetching ? (
          <div className={`${baseClass}__loading`}>
            <div className={`${baseClass}__loading-spinner`}></div>
            <span className={`${baseClass}__loading-text`}>
              Loading messages...
            </span>
          </div>
        ) : (
          <div className={`${baseClass}__messages`}>
            <div className={`${baseClass}__date`}>
              <div className={`${baseClass}__date-line`}></div>
              <span className={`${baseClass}__date-text`}>Today</span>
              <div className={`${baseClass}__date-line`}></div>
            </div>

            {messages.map((msg) => (
              <UIChatMessage
                key={msg.id}
                text={msg.text}
                timestamp={msg.timestamp}
                isOwn={msg.isOwn}
                sender={msg.sender}
                size="medium"
              />
            ))}

            <div ref={messagesEndRef} />
          </div>
        )}

        <UIChatInput
          placeholder="Type your message..."
          onSendMessage={handleSendMessage}
          onAttachFile={onAttachFile}
          size="medium"
          disabled={isPending || chatIsLoading || chatIsFetching}
        />
      </div>
    </div>
  );
};

export default ConversationView;
